import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch {
    return iso.slice(0, 10);
  }
};

const StatusPill = ({ status }) => {
  const s = (status || 'unknown').toLowerCase();
  const cls =
    s === 'complete'
      ? 'text-emerald-300 bg-emerald-500/15 border-emerald-500/30'
      : s === 'queued'
      ? 'text-amber-300 bg-amber-500/15 border-amber-500/30 animate-pulse'
      : s === 'failed'
      ? 'text-red-300 bg-red-500/15 border-red-500/30'
      : 'text-slate-400 bg-slate-500/15 border-slate-500/30';
  return (
    <span
      data-testid={`research-lib-status-${s}`}
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}
    >
      {s}
    </span>
  );
};

const TypePill = ({ type }) => {
  const cls =
    type === 'RESEARCH'
      ? 'text-cyan-300 bg-cyan-500/10 border-cyan-500/30'
      : 'text-violet-300 bg-violet-500/10 border-violet-500/30';
  return (
    <span
      data-testid={`research-lib-type-${type}`}
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border font-mono ${cls}`}
    >
      {type === 'RESEARCH' ? 'RESEARCH' : 'ABH CI'}
    </span>
  );
};

const BriefModal = ({ brief, type, onClose, onRerun, rerunning }) => {
  if (!brief) return null;
  const topic = brief.topic || brief.query_text || '(untitled)';
  const text = brief.brief_text || '';
  return (
    <div
      data-testid="research-lib-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-950 border border-slate-700 rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-800">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <TypePill type={type} />
              <StatusPill status={brief.status || 'unknown'} />
              <span className="text-[10px] text-slate-500">{fmtRel(brief.queued_at || brief.created_at)}</span>
            </div>
            <h3 data-testid="research-lib-modal-topic" className="text-base font-semibold text-white line-clamp-2">
              {topic}
            </h3>
          </div>
          <button
            type="button"
            data-testid="research-lib-modal-close"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-300 text-xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div
          data-testid="research-lib-modal-body"
          className="flex-1 overflow-y-auto p-5 text-sm text-slate-200 whitespace-pre-wrap font-light"
        >
          {text || (
            <p className="text-slate-500 italic">
              {brief.status === 'queued'
                ? 'Brief is still being synthesised — try again in 60-90 seconds.'
                : brief.status === 'failed'
                ? `Brief failed: ${brief.error || 'unknown error'}`
                : 'No content yet.'}
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-800">
          <button
            type="button"
            data-testid="research-lib-modal-rerun"
            onClick={() => onRerun(topic)}
            disabled={rerunning}
            className={`text-xs px-3 py-1.5 rounded border transition-colors ${
              rerunning
                ? 'border-slate-800 text-slate-600 cursor-not-allowed'
                : 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
            }`}
          >
            {rerunning ? 'Re-running…' : 'Run again'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-400 hover:bg-slate-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ResearchLibraryDrawer = ({ limit = 12 }) => {
  const [tab, setTab] = useState('research');
  const [research, setResearch] = useState(null);
  const [abhci, setAbhci] = useState(null);
  const [error, setError] = useState(null);
  const [openBrief, setOpenBrief] = useState(null);
  const [openType, setOpenType] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, a] = await Promise.all([
          axios.get(`${API}/tiny-tim/research`, { params: { limit } }),
          axios.get(`${API}/tiny-tim/abh-ci`, { params: { limit } }),
        ]);
        if (!cancelled) {
          setResearch(r.data);
          setAbhci(a.data);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load library');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [limit, reload]);

  const handleRerun = async (topic) => {
    if (!topic || !openType) return;
    setRerunning(true);
    try {
      const url = openType === 'RESEARCH' ? '/tiny-tim/research/rerun' : '/tiny-tim/abh-ci/rerun';
      await axios.post(`${API}${url}`, { topic });
      setReload((v) => v + 1);
      setOpenBrief(null);
    } catch (e) {
      setError(`Rerun failed: ${e?.message || 'unknown'}`);
    } finally {
      setRerunning(false);
    }
  };

  const items = useMemo(() => {
    if (tab === 'research') return research?.items || [];
    return abhci?.items || [];
  }, [tab, research, abhci]);

  const counts = {
    research: research?.count || 0,
    abhci: abhci?.count || 0,
  };

  if (error) {
    return (
      <div
        data-testid="research-lib-error"
        className="rounded-xl border border-red-900/40 bg-red-950/20 p-4 text-sm text-red-300"
      >
        Research library unavailable: {error}
      </div>
    );
  }

  if (!research && !abhci) {
    return (
      <div
        data-testid="research-lib-loading"
        className="rounded-xl border border-slate-800 bg-slate-950/40 p-4 text-sm text-slate-500"
      >
        Loading research library…
      </div>
    );
  }

  return (
    <>
      <div
        data-testid="research-lib"
        className="rounded-xl border border-slate-800 bg-slate-950/40 p-4"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Research library</p>
          <span className="text-[11px] text-slate-500">
            {counts.research + counts.abhci} brief{counts.research + counts.abhci === 1 ? '' : 's'}
          </span>
        </div>

        {/* Tabs */}
        <div className="flex bg-slate-950/60 border border-slate-800 rounded-lg p-0.5 mb-3 text-xs">
          <button
            type="button"
            data-testid="research-lib-tab-research"
            onClick={() => setTab('research')}
            className={`flex-1 px-2 py-1.5 rounded-md transition-colors ${
              tab === 'research'
                ? 'bg-cyan-600 text-white'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            Research <span className="opacity-60 ml-1">({counts.research})</span>
          </button>
          <button
            type="button"
            data-testid="research-lib-tab-abhci"
            onClick={() => setTab('abhci')}
            className={`flex-1 px-2 py-1.5 rounded-md transition-colors ${
              tab === 'abhci'
                ? 'bg-violet-600 text-white'
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            ABH CI <span className="opacity-60 ml-1">({counts.abhci})</span>
          </button>
        </div>

        {items.length === 0 ? (
          <p
            data-testid="research-lib-empty"
            className="text-sm text-slate-500 py-6 text-center"
          >
            {tab === 'research'
              ? 'No deep research briefs yet. Ask Tiny Tim "do deep research on …" to create one.'
              : 'No ABH competitive intel yet. Ask Tiny Tim about competitors in the GLP-1 space (or any vertical) to create one.'}
          </p>
        ) : (
          <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {items.map((it, idx) => {
              const id = it.brief_id || it.intel_id;
              const topic = it.topic || it.query_text || '(untitled)';
              const ts = it.queued_at || it.created_at;
              const type = tab === 'research' ? 'RESEARCH' : 'ABH_CI';
              return (
                <li
                  key={id || idx}
                  data-testid={`research-lib-row-${idx}`}
                  className="rounded-lg border border-slate-800/80 bg-slate-900/40 hover:bg-slate-900/80 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setOpenBrief(it);
                      setOpenType(type);
                    }}
                    className="w-full flex items-center gap-3 p-3 text-left"
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-slate-200 truncate">{topic}</span>
                      <span className="block text-[10px] text-slate-500">
                        {fmtRel(ts)}
                        {tab === 'research' && it.competitors_crawled != null && it.status === 'complete'
                          ? ` · ${it.competitors_crawled} competitors · ${it.news_pulled || 0} news`
                          : ''}
                        {tab === 'abhci' && it.competitors_analysed != null && it.status === 'complete'
                          ? ` · ${it.competitors_analysed} competitors`
                          : ''}
                      </span>
                    </span>
                    <StatusPill status={it.status} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <BriefModal
        brief={openBrief}
        type={openType}
        onClose={() => setOpenBrief(null)}
        onRerun={handleRerun}
        rerunning={rerunning}
      />
    </>
  );
};

export default ResearchLibraryDrawer;
